import { STREAMING_TIMEOUT } from "@root/constants";
import { useState, useEffect, useRef } from "react";

interface TypewriterOptions {
  /**
   * Delay between each character in milliseconds
   * @default 100
   */
  delay?: number;
  /**
   * Whether to loop the animation
   * @default false
   */
  loop?: boolean;
  /**
   * Initial delay before starting the animation in milliseconds
   * @default 0
   */
  startDelay?: number;
  /**
   * Whether to disable the typewriter animation effect
   * @default false
   */
  disableAnimation?: boolean;
  /**
   * Whether the content is streaming (updates frequently)
   * @default false
   */
  isStreaming?: boolean;
}

interface TypewriterResult {
  /**
   * The current displayed text
   */
  displayText: string;
  /**
   * Whether the typing animation is complete
   */
  isComplete: boolean;
  /**
   * Function to start the animation over
   */
  restart: () => void;
}

const useTypewriterEffect = (
  text: string,
  options: TypewriterOptions = {}
): TypewriterResult => {
  const {
    delay = 100,
    loop = false,
    startDelay = 0,
    disableAnimation = false,
    isStreaming = false,
  } = options;

  const [displayText, setDisplayText] = useState<string>(
    disableAnimation ? text : ""
  );
  const [isComplete, setIsComplete] = useState<boolean>(disableAnimation);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [streamingTimeoutId, setStreamingTimeoutId] =
    useState<NodeJS.Timeout | null>(null);

  // Keep track of the previous text to detect changes
  const previousText = useRef<string>("");
  const disableAnimationRef = useRef<boolean>(disableAnimation);
  const displayTextRef = useRef<string>(displayText);
  const currentIndexRef = useRef<number>(currentIndex);

  // Keep refs in sync with state
  useEffect(() => {
    displayTextRef.current = displayText;
  }, [displayText]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    // Reflect changes to disableAnimation dynamically
    if (disableAnimation !== disableAnimationRef.current) {
      disableAnimationRef.current = disableAnimation;

      if (disableAnimation) {
        setDisplayText(text);
        setIsComplete(true);
      } else {
        // Re-enabled on text that is already on screen (e.g. a reply restored
        // after a refresh that is still streaming): keep it and type only what
        // arrives next. Refs are set too so the effect below sees the same state.
        setDisplayText(text);
        setCurrentIndex(text.length);
        displayTextRef.current = text;
        currentIndexRef.current = text.length;
        previousText.current = text;
        setIsComplete(true);
      }
    }
  }, [disableAnimation, text]);

  useEffect(() => {
    if (disableAnimation) {
      return;
    }

    if (text === previousText.current) {
      return;
    }

    const previousTextValue = previousText.current;
    const currentDisplayText = displayTextRef.current;
    const currentIndexValue = currentIndexRef.current;

    const isTextAppended =
      previousTextValue && text.startsWith(previousTextValue);
    const isTextContinuing =
      currentDisplayText && text.startsWith(currentDisplayText);
    const isTextTruncated =
      previousTextValue && previousTextValue.startsWith(text);

    // In streaming mode, handle content updates more gracefully
    if (isStreaming) {
      if (isTextAppended || isTextContinuing) {
        // Content is being appended or continuing - don't reset
        // Update currentIndex to match displayText length if we're behind
        if (currentDisplayText && text.startsWith(currentDisplayText)) {
          const newIndex = Math.max(
            currentIndexValue,
            currentDisplayText.length
          );
          if (newIndex !== currentIndexValue) {
            setCurrentIndex(newIndex);
          }
        }
        // If text was appended but we haven't caught up yet, don't interfere with typing

        // Clear existing timeout
        if (streamingTimeoutId) {
          clearTimeout(streamingTimeoutId);
        }

        // Set a new timeout - if no new content comes in within 2 seconds, consider streaming complete
        const timeoutId = setTimeout(() => {
          // Use ref to get latest value
          if (currentIndexRef.current >= text?.length) {
            setIsComplete(true);
          }
        }, STREAMING_TIMEOUT);

        setStreamingTimeoutId(timeoutId);
      } else if (isTextTruncated) {
        // Text was truncated (content replaced with shorter version)
        // This shouldn't happen during streaming, but handle it gracefully
        // Don't reset, just adjust currentIndex if needed
        if (currentIndexValue > text.length) {
          setCurrentIndex(text.length);
        }
      } else {
        // Text changed in a way that's not a continuation
        // Only reset if it's a completely different message (not just formatting changes)
        // Check if it's a significant change (more than just whitespace/formatting)
        const textWithoutWhitespace = text?.replace(/\s+/g, "");
        const previousWithoutWhitespace = previousTextValue?.replace(
          /\s+/g,
          ""
        );

        // Only reset if the core content is different (not just formatting)
        // Check if at least 50 chars of the beginning match (to avoid resetting on minor changes)
        const minCheckLength = Math.min(
          50,
          previousWithoutWhitespace?.length || 0
        );
        const shouldReset =
          textWithoutWhitespace &&
          previousWithoutWhitespace &&
          minCheckLength > 0 &&
          !textWithoutWhitespace.startsWith(
            previousWithoutWhitespace.substring(0, minCheckLength)
          );

        if (shouldReset) {
          // Completely different content - reset
          setDisplayText("");
          setCurrentIndex(0);
          setIsComplete(false);

          // Clear streaming timeout
          if (streamingTimeoutId) {
            clearTimeout(streamingTimeoutId);
            setStreamingTimeoutId(null);
          }
        }
        // If it's not a significant change, just update previousText and continue
      }
    } else {
      // Not in streaming mode
      if (typeof text === "string" && text?.startsWith(currentDisplayText)) {
        // Continue typing from current position
        setCurrentIndex(currentDisplayText?.length);
        setIsComplete(false);
      } else {
        // Reset if the new text is completely different
        setDisplayText("");
        setCurrentIndex(0);
        setIsComplete(false);

        // Clear streaming timeout
        if (streamingTimeoutId) {
          clearTimeout(streamingTimeoutId);
          setStreamingTimeoutId(null);
        }
      }
    }

    previousText.current = text;
  }, [text, disableAnimation, isStreaming, streamingTimeoutId]);

  useEffect(() => {
    if (disableAnimation) {
      return;
    }

    if (currentIndex >= text.length) {
      setIsComplete(true);
      if (loop) {
        setTimeout(() => {
          setDisplayText("");
          setCurrentIndex(0);
          setIsComplete(false);
        }, delay * 3); // Add a delay before looping
      }
      return;
    }

    // Schedule the next character to be displayed
    const timeoutId = setTimeout(() => {
      setDisplayText((prev) => prev + text[currentIndex]);
      setCurrentIndex((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [currentIndex, delay, text, disableAnimation, loop]);

  const restart = () => {
    if (disableAnimation) {
      setDisplayText(text);
      setIsComplete(true);
      return;
    }

    setDisplayText("");
    setCurrentIndex(0);
    setIsComplete(false);

    // Clear streaming timeout
    if (streamingTimeoutId) {
      clearTimeout(streamingTimeoutId);
      setStreamingTimeoutId(null);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (streamingTimeoutId) {
        clearTimeout(streamingTimeoutId);
      }
    };
  }, [streamingTimeoutId]);

  return { displayText, isComplete, restart };
};

export default useTypewriterEffect;
